import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiClient } from '../services/api';
import { isApiErrorStatus } from '../services/api/http';
import { User, AuthContextType, SubscriptionStatusDto } from '../types';
import { STORAGE_KEYS } from '../utils/constants';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionStatusDto | null>(null);

  const loadSubscription = async (currentUser: User) => {
    if (!currentUser.companyId || currentUser.role === 'SUPER_ADMIN') {
      setSubscription(null);
      return;
    }
    try {
      setSubscription(await apiClient.getCurrentSubscription());
    } catch {
      // Subscription status must never prevent an authenticated user from
      // opening the application. The backend remains the source of truth for
      // mutation authorization.
      setSubscription(null);
    }
  };

  useEffect(() => {
    // Authentication is cookie-backed. The cached user only tells us that a
    // session existed previously; the HttpOnly cookies remain the source of
    // truth and are sent automatically by the API client.
    const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
    // Remove tokens left by versions that used localStorage bearer auth.
    localStorage.removeItem(STORAGE_KEYS.TOKEN);

    // A fresh visitor has no authenticated cookie-backed session yet. Do not
    // make an anonymous /auth/me request; it would only produce a 401 on the
    // public login page.
    if (!storedUser) {
      setIsLoading(false);
      return;
    }

    let cachedUser: User | null = null;
    try {
      cachedUser = JSON.parse(storedUser) as User;
      setUser(cachedUser);
    } catch {
      localStorage.removeItem(STORAGE_KEYS.USER);
    }

    apiClient.getCurrentUser()
      .then((currentUser) => {
        setUser(currentUser);
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(currentUser));
        void loadSubscription(currentUser);
      })
      .catch((error: unknown) => {
        // A transient network/429/5xx response must not look like an expired
        // session. Only an explicit unauthorized response invalidates the
        // cookie-backed session.
        if (isApiErrorStatus(error, 401)) {
          setUser(null);
          localStorage.removeItem(STORAGE_KEYS.USER);
        } else if (cachedUser) {
          setUser(cachedUser);
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  const login = async (usernameOrEmail: string, password: string): Promise<User> => {
    try {
      setIsLoading(true);
      const response = await apiClient.login({ usernameOrEmail, password });

      setToken(null);
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      
      // Fetch full user details including company information
      const fullUserData = await apiClient.getUser(response.userId);
      
      setUser(fullUserData);
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(fullUserData));
      await loadSubscription(fullUserData);
      return fullUserData;
    } catch (error: unknown) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await apiClient.logout();
    } catch {
      // Local session cleanup is guaranteed even if the remote logout fails.
    } finally {
      // Clear local state regardless of API call success
      setUser(null);
      setToken(null);
      setSubscription(null);
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
    }
  };

  const updateUser = (updatedUser: User): void => {
    setUser(updatedUser);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updatedUser));
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    updateUser,
    isAuthenticated: !!user,
    isLoading,
    subscription,
    isReadOnly: subscription?.readOnly ?? false,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
